import React from 'react'
import { useTranslation } from 'react-i18next'

import { FamilyStatus, ProductClassDesignSummaryResponse } from 'core'

import { Alert, Card, Col, ExpandableTable, Icon, Row } from 'common/components'
import { parseZoneFormatV2 } from 'common/dateFormatters'

import { differentAllowanceColumnsV2, sameAllowanceColumns } from '../AgeAllowanceColumns'

type SubCollapseTypesProps = {
  benefitStartDate?: string
  benefitEndDate?: string
  productClassDetails: ProductClassDesignSummaryResponse
}

const SubCollapse = ({ benefitStartDate, benefitEndDate, productClassDetails }: SubCollapseTypesProps) => {
  const { t: translate } = useTranslation()

  const icons = [
    <Icon className="fal fa-user  ml-4 mr-8" />,
    <Icon className="fal fa-user-friends mr-8" />,
    <Icon className="fal fa-user-friends mr-8" />,
    <Icon className="fal fa-users mr-8" />,
  ]

  const getAllowanceType = () => {
    if (productClassDetails.familyAgeAllowances.length == 0 && productClassDetails.offerSameAllowance) {
      return 'Same'
    } else if (productClassDetails.familyAgeAllowances.length == 0 && !productClassDetails.offerSameAllowance) {
      return 'Family Status'
    } else if (productClassDetails.familyAgeAllowances.length > 0 && productClassDetails.offerSameAllowance) {
      return 'Age'
    } else if (productClassDetails.familyAgeAllowances.length > 0 && !productClassDetails.offerSameAllowance) {
      return 'Age + Family Status'
    }
  }

  const showAgeAllowanceTable = productClassDetails.familyAgeAllowances.length > 0

  return (
    <>
      <Card className="design_classes_collapse_v2">
        <Row>
          <Col span={12}>
            <span className="design_classes_collapse_title">
              {translate('admin-dashboard.benefit-details.summary.eligible-expenses')}
            </span>
            <span className="design_classes_collapse_data">{productClassDetails.reimbursementProduct.name}</span>
          </Col>
          <Col span={12}>
            <span className="design_classes_collapse_title">
              {translate('admin-dashboard.benefit-details.summary.allowance-type')}
            </span>
            <span className="design_classes_collapse_data">{getAllowanceType()}</span>
          </Col>
        </Row>
      </Card>
      <Card className="design_classes_collapse_v2">
        <Row className="mt-16">
          <Col span={12}>
            <span className="design_classes_collapse_title">
              {translate('admin-dashboard.benefit-details.summary.benefits-period')}
            </span>
            <span className="design_classes_collapse_data">
              {parseZoneFormatV2(benefitStartDate)} - {parseZoneFormatV2(benefitEndDate)}
            </span>
          </Col>
          <Col>
            <span className="design_classes_collapse_title">
              {translate('admin-dashboard.benefit-details.summary.waiting period')}
            </span>
            <span className="design_classes_collapse_data">
              {productClassDetails?.waitingPeriod !== 0
                ? `${productClassDetails?.waitingPeriod} ${translate(
                    'admin-dashboard.benefit-details.summary.waiting-period-value-label',
                  )}`
                : `None`}
            </span>
          </Col>
        </Row>
      </Card>

      {!showAgeAllowanceTable && productClassDetails.designCompleted && (
        <Card className="mt-16">
          <Row>
            <Col span={24}>
              <p className="design_classes_collapse_allowances_title">
                {translate('admin-dashboard.benefit-details.summary.allowances')}
              </p>
            </Col>
          </Row>
          <Row className="mt-8">
            <Col span={18}>
              <strong className="design_classes_collapse_allowances mb-8">
                <span>{icons[0]}&nbsp;&nbsp; Single</span>
              </strong>
            </Col>
            <Col span={6} className="text-right">
              <span className="design_classes_collapse_allowance_amount">
                ${productClassDetails.familyAllowances.find((x) => x.familyId == FamilyStatus.Single)?.amount}
              </span>
            </Col>
          </Row>
          <Row className="mt-8">
            <Col span={18}>
              <strong className="design_classes_collapse_allowances mb-8">
                <span>{icons[1]}&nbsp;&nbsp; Single with dependents</span>
              </strong>
            </Col>
            <Col span={6} className="text-right">
              <span className="design_classes_collapse_allowance_amount">
                $
                {
                  productClassDetails.familyAllowances.find((x) => x.familyId == FamilyStatus.SingleWithDependents)
                    ?.amount
                }
              </span>
            </Col>
          </Row>
          <Row className="mt-8">
            <Col span={18}>
              <strong className="design_classes_collapse_allowances mb-8">
                <span>{icons[2]}&nbsp;&nbsp; Married</span>
              </strong>
            </Col>
            <Col span={6} className="text-right">
              <span className="design_classes_collapse_allowance_amount">
                $
                {
                  productClassDetails.familyAllowances.find((x) => x.familyId == FamilyStatus.SingleWithDependents)
                    ?.amount
                }
              </span>
            </Col>
          </Row>
          <Row className="mt-8">
            <Col span={18}>
              <strong className="design_classes_collapse_allowances mb-8">
                <span>{icons[3]}&nbsp;&nbsp; Married with dependents</span>
              </strong>
            </Col>
            <Col span={6} className="text-right">
              <span className="design_classes_collapse_allowance_amount">
                $
                {
                  productClassDetails.familyAllowances.find((x) => x.familyId == FamilyStatus.MarriedWithDependents)
                    ?.amount
                }
              </span>
            </Col>
          </Row>
        </Card>
      )}

      {showAgeAllowanceTable && productClassDetails.designCompleted && (
        <>
          <Card className="mt-16">
            <Row>
              <Col span={24}>
                <p className="design_classes_collapse_allowances_title">
                  {translate('admin-dashboard.benefit-details.summary.allowances')}
                </p>
              </Col>
            </Row>
            <Row>
              <ExpandableTable
                className={'design_classes_collapse_age_allowance w-100'}
                dataSource={productClassDetails.familyAgeAllowances}
                loading={false}
                columns={
                  productClassDetails.offerSameAllowance
                    ? sameAllowanceColumns(productClassDetails.familyAgeAllowances)
                    : differentAllowanceColumnsV2(productClassDetails.familyAgeAllowances)
                }
                pagination={false}
              />
            </Row>
          </Card>
          <Alert
            type="info"
            showIcon
            className="mt-16"
            description={
              'Allowances for ages in between your oldest and youngest employee age are calculated based on the difference between the oldest and youngest allowances divided by the difference between oldest and youngest ages. '
            }
          />
        </>
      )}
    </>
  )
}

export default SubCollapse
